import { FormattedMessage } from 'react-intl';

import AdjustmentsTable from 'src/features/pricingPlan/components/AdjustmentsTable';
import PricingPlanCapsTable from 'src/features/pricingPlan/components/PricingPlanCapsTable';
import PricingPlanTiersTable from 'src/features/pricingPlan/components/PricingPlanTiersTable';
import PricingPlanUsedInTable from 'src/features/pricingPlan/components/PricingPlanUsedInTable';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';

import PricingPlanRatesTable from './PricingPlanRatesTable';
import { CURRENCY } from 'src/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

type PricingPlanDashboardTablesProps = {
    isPerFixedTick: boolean | null;
    isPerFixedPrice: boolean | null;
    currency?: CURRENCY;
    pricingPlanIdentity?: string;
};

const PricingPlanDashboardTables = ({
    isPerFixedTick,
    isPerFixedPrice,
    currency,
    pricingPlanIdentity,
}: PricingPlanDashboardTablesProps) => {
    const accessPricingPlanTickRates = useHasPermissions([PERMISSION.ACCESS_PRICING_PLAN_TICK_RATES]);
    const accessPricingPlanTiers = useHasPermissions([PERMISSION.ACCESS_PRICING_PLAN_TIERS]);
    const accessPricingPlanCaps = useHasPermissions([PERMISSION.ACCESS_PRICING_PLAN_CAPS]);
    const accessPricingPlanPeriodAdjustments = useHasPermissions([PERMISSION.ACCESS_PRICING_PLAN_PERIOD_ADJUSTMENTS]);

    const permissionUsedInTable = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const defaultTabValue = (() => {
        if (accessPricingPlanTickRates && isPerFixedTick) return 'rates';
        if (accessPricingPlanTiers && !isPerFixedTick && !isPerFixedPrice) return 'tiers';
        if (accessPricingPlanCaps && !isPerFixedTick && !isPerFixedPrice) return 'caps';
        if (accessPricingPlanPeriodAdjustments) return 'adjustments';
        if (permissionUsedInTable) return 'used-in';
    })();

    return (
        <div className="grid flex-3 grid-cols-1">
            <Tabs defaultValue={defaultTabValue}>
                <TabsList>
                    {accessPricingPlanTickRates && isPerFixedTick && (
                        <TabsTrigger value="rates">
                            <FormattedMessage {...pricingPlanMessages.rates} />
                        </TabsTrigger>
                    )}

                    {accessPricingPlanTiers && !isPerFixedTick && !isPerFixedPrice && (
                        <TabsTrigger value="tiers">
                            <FormattedMessage {...pricingPlanMessages.tiers} />
                        </TabsTrigger>
                    )}

                    {accessPricingPlanCaps && !isPerFixedTick && !isPerFixedPrice && (
                        <TabsTrigger value="caps">
                            <FormattedMessage {...pricingPlanMessages.caps} />
                        </TabsTrigger>
                    )}

                    {accessPricingPlanPeriodAdjustments && (
                        <TabsTrigger value="adjustments">
                            <FormattedMessage {...pricingPlanMessages.adjustments} />
                        </TabsTrigger>
                    )}

                    {permissionUsedInTable && (
                        <TabsTrigger value="used-in">
                            <FormattedMessage {...pricingPlanMessages.usedIn} />
                        </TabsTrigger>
                    )}
                </TabsList>

                {accessPricingPlanTickRates && isPerFixedTick && (
                    <TabsContent value="rates" className="w-full">
                        <PricingPlanRatesTable />
                    </TabsContent>
                )}

                {accessPricingPlanTiers && !isPerFixedTick && !isPerFixedPrice && (
                    <TabsContent value="tiers">
                        <PricingPlanTiersTable currency={currency} />
                    </TabsContent>
                )}

                {accessPricingPlanCaps && !isPerFixedTick && !isPerFixedPrice && (
                    <TabsContent value="caps">
                        <PricingPlanCapsTable currency={currency} />
                    </TabsContent>
                )}

                {accessPricingPlanPeriodAdjustments && (
                    <TabsContent value="adjustments">
                        <AdjustmentsTable currency={currency} />
                    </TabsContent>
                )}
                {permissionUsedInTable && (
                    <TabsContent value="used-in">
                        <PricingPlanUsedInTable pricingPlanIdentity={pricingPlanIdentity} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
};

export default PricingPlanDashboardTables;
